import { createStore } from "zustand/vanilla";
import { devtools } from "zustand/middleware";

// external dependencies
import type { TApiODIN, TUserInfo } from "@/apis/odin/apiODIN";
import type { TApiAuth } from "@/apis/auth/apiAuth";
import type { TApiDatadog } from "@/apis/datadog/apiDatadog";
import type { TApiThirdParty } from "@/apis/apiThirdParty/apiThirdParty";
import type { TApiZendesk } from "@/apis/zendesk/apiZendesk";
import type { TApiAps } from "@/apis/proda/aps";
import type { TApiUserpilot } from "@/apis/userpilot/apiUserpilot";
// store types
import type { TStateCreator } from "@/store";

// store slices
import type { TSliceTableMappings } from "./tableMappings/sliceTableMappings";
import type { TSliceUploadFile } from "./uploadFile/sliceUploadFile";
import type { TSliceEditAccounts } from "./editAccounts/sliceEditAccounts";
import type { TSliceDashboardAnalytics } from "./analytics/sliceDashboardAnalytics";
import type { TSliceAdminDashboard } from "./adminDashboard/sliceAdminDashboard";
import type { TSliceNotifications } from "./notifications/sliceNotifications";
import type { TSliceDeals } from "./deals/sliceDeals";
import type { TSliceDealOverview } from "./deals/sliceDealOverview";
import type { TSliceExport } from "./export/sliceExport";
import type { TSliceTableExtractions } from "./tableExtractions/sliceTableExtractions";
import type { TSliceConcurrentUsers } from "./concurrentUsers/sliceConcurrentUsers";
import type { TSliceGridState } from "./gridState/sliceGridState";
import type { TSliceLogin } from "./login/sliceLogin";
import type { TSliceSettings } from "./settings/sliceSettings";
import type { TSliceAnnualizedExtraction } from "./extractionAnnualized/sliceAnnualizedExtraction";
import type { TSliceAuth } from "./auth/sliceAuth";
import type { TSliceInitialConfig } from "./initialConfig/sliceInitialConfig";
import type { TSliceMetrics } from "./metrics/sliceMetrics";
import type { TSliceDealFileExtractionSelection } from "./fileSelect/sliceDealFileExtractionSelection";
import type { TSliceThirdPartyDoc } from "./thirdpartydoc/sliceThirdPartyDoc";
import type { TSliceThirdPartyAuth } from "./thirdpartyAuth/sliceThirdpartyAuth";
import type { TSliceAddExtraction } from "./addExtraction/sliceAddExtraction";
import type { TSliceUserConsent } from "./userConsent/sliceUserConsent";
import type { TSliceUiSettings } from "./uiSettings/sliceUiSettings";
import { createSliceUiSettings } from "./uiSettings/sliceUiSettings";
import type { TSliceZendesk } from "./zendesk/sliceZendesk";
import type { TSliceAps } from "./proda/sliceAps";
import { createSliceAps } from "./proda/sliceAps";
import type { TSliceCustomChartOfAccountsMappings } from "./sliceCustomChartOfAccountsMappings";
import type { TSliceEditExtractionRows } from "./editExtractionRows/sliceEditExtractionRows";
import { createSliceUploadFile } from "./uploadFile/sliceUploadFile";
import { createSliceAddExtraction } from "./addExtraction/sliceAddExtraction";
import { createSliceTableMappings } from "./tableMappings/sliceTableMappings";
import { createSliceEditAccounts } from "./editAccounts/sliceEditAccounts";
import { createSliceDashboardAnalytics } from "./analytics/sliceDashboardAnalytics";
import { createSliceAdminDashboard } from "./adminDashboard/sliceAdminDashboard";
import { createSliceNotifications } from "./notifications/sliceNotifications";
import { createSliceDeals } from "./deals/sliceDeals";
import { createSliceDealOverview } from "./deals/sliceDealOverview";
import { createSliceExport } from "./export/sliceExport";
import { createSliceTableExtractions } from "./tableExtractions/sliceTableExtractions";
import { createSliceConcurrentUsers } from "./concurrentUsers/sliceConcurrentUsers";
import { createSliceGridState } from "./gridState/sliceGridState";
import { createSliceLogin } from "./login/sliceLogin";
import { createSliceSettings } from "./settings/sliceSettings";
import { createSliceAnnualizedExtraction } from "./extractionAnnualized/sliceAnnualizedExtraction";
import { createSliceAuth } from "./auth/sliceAuth";
import { createSliceInitialConfig } from "./initialConfig/sliceInitialConfig";
import { createSliceMetrics } from "./metrics/sliceMetrics";
import { createSliceDealFileExtractionSelection } from "./fileSelect/sliceDealFileExtractionSelection";
import { createSliceThirdPartyDoc } from "./thirdpartydoc/sliceThirdPartyDoc";
import { createSliceThirdPartyAuth } from "./thirdpartyAuth/sliceThirdpartyAuth";
import { createSliceUserConsent } from "./userConsent/sliceUserConsent";
import { createSliceZendesk } from "./zendesk/sliceZendesk";
import { createSliceCustomChartOfAccountsMappings } from "./sliceCustomChartOfAccountsMappings";

import { routerPaths } from "@/ui/router/routerPaths";
import { createSliceEditExtractionRows } from "./editExtractionRows/sliceEditExtractionRows";
import {
    createSliceUploadCoA,
    type TSliceUploadCoA,
} from "./uploadCoA/sliceUploadCoA";
export type TStoreRoot = TSliceAuth &
    TSliceThirdPartyAuth &
    TSliceThirdPartyDoc &
    TSliceAnnualizedExtraction &
    TSliceUploadFile &
    TSliceTableMappings &
    TSliceEditAccounts &
    TSliceNotifications &
    TSliceGridState &
    TSliceDashboardAnalytics &
    TSliceDeals &
    TSliceDealOverview &
    TSliceExport &
    TSliceTableExtractions &
    TSliceConcurrentUsers &
    TSliceLogin &
    TSliceInitialConfig &
    TSliceSettings &
    TSliceMetrics &
    TSliceDealFileExtractionSelection &
    TSliceAddExtraction &
    TSliceUserConsent &
    TSliceUiSettings &
    TSliceZendesk &
    TSliceAdminDashboard &
    TSliceCustomChartOfAccountsMappings &
    TSliceEditExtractionRows &
    TSliceAps &
    TSliceUploadCoA;

const checkSlicesKeyForDuplicates = <T extends Record<string, unknown>>(
    slices: T[],
) => {
    const keys = slices.map(slice => Object.keys(slice)).flat();
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);

    if (duplicates.length > 0) {
        throw new Error(
            `Duplicate keys found in Zustand store slices: ${duplicates.join(", ")}`,
        );
    }
};

export const createStoreRoot = ({
    apiODIN,
    apiAuth,
    apiThirdPartyAuth,
    apiThirdParty,
    apiDatadog,
    apiZendesk,
    apiUserpilot,
    apiAps,
    navigate,
    getUserDataSnapshot,
}: {
    apiODIN: TApiODIN;
    apiAuth: TApiAuth;
    apiThirdPartyAuth: TApiAuth;
    apiThirdParty: TApiThirdParty;
    apiDatadog: TApiDatadog;
    apiZendesk: TApiZendesk;
    apiUserpilot: TApiUserpilot;
    apiAps: TApiAps;
    getUserDataSnapshot?: () => {
        userInfo?: TUserInfo | null;
        userSelectedDatabases?: number[];
        userSelectedModel?: string;
        userConsent?: { userConsentValid?: boolean };
        isRentRollUploadEnabled?: boolean;
    } | null;
    navigate: {
        toAppEntrypoint: () => void;
        toPageDealOverviewDocuments: ({
            databaseId,
            projectId,
        }: {
            databaseId: string;
            projectId: string;
        }) => void;
        toPageExtractionReview: (params: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) => void;
        toPageExtractionMappings: (params: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) => void;
        navigateToPageExtractions: (params: {
            projectId: string;
            databaseId: string;
            extractionId: string;
        }) => void;
        toReferer: ({ url }: { url: string | null }) => void;
        toChartOfAccountsEditDefinition: ({ coaId }: { coaId: string }) => void;
        toChartOfAccountsEditDetails: ({ coaId }: { coaId: string }) => void;
    };
}) => {
    const stateCreator: TStateCreator<TStoreRoot> = (set, getState, store) => {
        const sliceNotifications = createSliceNotifications()(
            set,
            getState,
            store,
        );

        const sliceLogin = createSliceLogin({
            apiODIN,
            navigateToReferer: navigate.toReferer,
        })(set, getState, store);
        const sliceAuth = createSliceAuth({
            apiAuth,
            sliceLogin,
        })(set, getState, store);
        const sliceInitialConfig = createSliceInitialConfig({
            apiODIN,
            initializeAuth: sliceAuth.initializeAuth,
        })(set, getState, store);
        const sliceUserConsent = createSliceUserConsent({})(
            set,
            getState,
            store,
        );
        const sliceAnnualizedExtraction = createSliceAnnualizedExtraction()(
            set,
            getState,
            store,
        );
        const sliceEditAccounts = createSliceEditAccounts()(
            set,
            getState,
            store,
        );
        const sliceDashboardAnalytics = createSliceDashboardAnalytics({})(
            set,
            getState,
            store,
        );
        const sliceDealFileExtractionSelection =
            createSliceDealFileExtractionSelection()(set, getState, store);
        const sliceDeals = createSliceDeals()(set, getState, store);
        const sliceExport = createSliceExport()(set, getState, store);
        const sliceDealOverview = createSliceDealOverview()(
            set,
            getState,
            store,
        );
        const sliceTableMappings = createSliceTableMappings({
            getExtractedRows: () => getState().extractionRowsLoaded,
            getStandardizedExtractionErrors: () =>
                getState().extractionStandardizedErrors,
        })(set, getState, store);
        const sliceEditExtractionRows = createSliceEditExtractionRows({})(
            set,
            getState,
            store,
        );
        const sliceUploadCoA = createSliceUploadCoA()(set, getState, store);
        const sliceAddExtraction = createSliceAddExtraction()(
            set,
            getState,
            store,
        );
        const sliceUploadFiles = createSliceUploadFile({
            getRentrollUploadEnabled: () =>
                getUserDataSnapshot?.()?.isRentRollUploadEnabled ?? false,
        })(set, getState, store);
        const sliceTableExtractions = createSliceTableExtractions({})(
            set,
            getState,
            store,
        );
        const sliceConcurrentUsers = createSliceConcurrentUsers({
            apiODIN,
        })(set, getState, store);
        const sliceGridState = createSliceGridState({})(set, getState, store);
        const sliceSettings = createSliceSettings({})(set, getState, store);
        const sliceUiSettings = createSliceUiSettings({})(set, getState, store);
        const sliceThirdPartyAuth = createSliceThirdPartyAuth({
            apiThirdPartyAuth,
            apiThirdParty,
            navigateToReferer: navigate.toReferer,
            thirdPartyDocUrl: routerPaths.thirdPartyDoc.default(),
        })(set, getState, store);
        const sliceThirdPartyDoc = createSliceThirdPartyDoc({
            apiThirdParty,
            apiThirdPartyAuth,
            navigateToReferer: navigate.toReferer,
            addNotificationWithDefaultTimeout:
                sliceNotifications.addNotificationWithDefaultTimeout,
            getAccessToken:
                sliceThirdPartyAuth.getThirdPartyTokenOrRedirectToLogin,
            onInitializeSuccess: sliceThirdPartyAuth.onInitializeSuccess,
            getUser: sliceThirdPartyAuth.getUser,
            logoutThirdParty: sliceThirdPartyAuth.logoutThirdParty,
        })(set, getState, store);
        const sliceMetrics = createSliceMetrics({
            apiDatadog,
            apiUserpilot,
            getUserInfo: () => getUserDataSnapshot?.()?.userInfo ?? null,
        })(set, getState, store);
        const sliceZendesk = createSliceZendesk({
            apiZendesk,
        })(set, getState, store);

        const sliceAps = createSliceAps({
            apiAps,
        })(set, getState, store);

        const sliceAdminDashboard = createSliceAdminDashboard({})(
            set,
            getState,
            store,
        );
        const sliceCustomChartOfAccountsMappings =
            createSliceCustomChartOfAccountsMappings()(set, getState, store);

        checkSlicesKeyForDuplicates([
            sliceAuth,
            sliceThirdPartyAuth,
            sliceThirdPartyDoc,
            sliceAnnualizedExtraction,
            sliceUploadFiles,
            sliceEditExtractionRows,
            sliceTableMappings,
            sliceEditAccounts,
            sliceDashboardAnalytics,
            sliceNotifications,
            sliceDeals,
            sliceDealOverview,
            sliceExport,
            sliceTableExtractions,
            sliceConcurrentUsers,
            sliceGridState,
            sliceLogin,
            sliceSettings,
            sliceUiSettings,
            sliceMetrics,
            sliceDealFileExtractionSelection,
            sliceAddExtraction,
            sliceUserConsent,
            sliceInitialConfig,
            sliceZendesk,
            sliceAdminDashboard,
            sliceCustomChartOfAccountsMappings,
            sliceUploadCoA,
            sliceAps,
        ]);

        return {
            ...sliceAuth,
            ...sliceThirdPartyAuth,
            ...sliceThirdPartyDoc,
            ...sliceAnnualizedExtraction,
            ...sliceLogin,
            ...sliceUploadFiles,
            ...sliceEditExtractionRows,
            ...sliceTableMappings,
            ...sliceEditAccounts,
            ...sliceDashboardAnalytics,
            ...sliceNotifications,
            ...sliceGridState,
            ...sliceDeals,
            ...sliceDealOverview,
            ...sliceExport,
            ...sliceTableExtractions,
            ...sliceConcurrentUsers,
            ...sliceSettings,
            ...sliceUiSettings,
            ...sliceMetrics,
            ...sliceDealFileExtractionSelection,
            ...sliceAddExtraction,
            ...sliceUserConsent,
            ...sliceInitialConfig,
            ...sliceZendesk,
            ...sliceAdminDashboard,
            ...sliceCustomChartOfAccountsMappings,
            ...sliceUploadCoA,
            ...sliceAps,
        };
    };

    const store = createStore(
        devtools(stateCreator, {
            serialize: {
                options: {
                    map: true,
                },
            },
        }),
    );

    return store;
};
